// ─────────────────────────────────────────────────────────────────────────────
// BSRTracker.jsx
// BSR Tracking page with history chart and summary table
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { RefreshCw } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── BSR Trend Badge ──────────────────────────────────────────────────────────
export function BSRTrendBadge({ trend }) {
  const config = {
    IMPROVED: { label: "↑ BSR Improved", bg: "bg-green-100", text: "text-green-700" },
    WORSENED: { label: "↓ BSR Worsened", bg: "bg-red-100", text: "text-red-700" },
    STABLE: { label: "→ Stable", bg: "bg-gray-100", text: "text-gray-600" },
    UP: { label: "↑ BSR Improved", bg: "bg-green-100", text: "text-green-700" },
    DOWN: { label: "↓ BSR Worsened", bg: "bg-red-100", text: "text-red-700" },
    UNKNOWN: { label: "? No Data", bg: "bg-gray-50", text: "text-gray-400" },
  };
  const c = config[trend] || config.UNKNOWN;
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}


// ─── BSR Chart for a single ASIN ─────────────────────────────────────────────
export function BSRHistoryChart({ asin, productName, days = 90 }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(days);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/bsr/history/${asin}?days=${period}`)
      .then(res => {
        // Format for recharts — lower BSR = better, so invert Y axis
        const chartData = (res.data.history || []).map(h => ({
          date: h.recorded_at?.split('T')[0] || h.date,
          bsr: h.bsr,
          est_units: h.estimated_monthly_units,
        }));
        setData(chartData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [asin, period]);

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-gray-800">{productName} — BSR History</h3>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              className={`px-3 py-1 text-xs rounded-full border 
                ${period === d
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-gray-400">
          Loading BSR data...
        </div>
      ) : data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-gray-400">
          No BSR history yet — sync pending
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              tickFormatter={d => d?.slice(5)}
            />
            {/* Invert Y axis — lower rank = better = top of chart */}
            <YAxis
              reversed={true}
              tick={{ fontSize: 10 }}
              tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
            />
            <Tooltip
              formatter={(value, name) => [
                name === "bsr"
                  ? `Rank #${value?.toLocaleString()}`
                  : `~${value} units/mo`,
                name === "bsr" ? "BSR" : "Est. Units"
              ]}
            />
            <Line
              type="monotone"
              dataKey="bsr"
              stroke="#2E75B6"
              strokeWidth={2}
              dot={false}
              name="BSR"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
      <p className="text-xs text-gray-400 mt-1">
        * Lower rank = better. Chart inverted for readability.
      </p>
    </div>
  );
}


// ─── BSR Summary Table (Products Page addition) ───────────────────────────────
export function BSRSummaryTable() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");    // all / own / competition
  const [category, setCategory] = useState("all");
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchBSRData();
  }, []);

  const fetchBSRData = async () => {
    try {
      const res = await axios.get(`${API}/bsr/current`);
      setProducts(res.data.products || res.data || []);
    } catch (error) {
      console.error("Error fetching BSR data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/bsr/sync`);
      toast.success("BSR data synced successfully");
      fetchBSRData();
    } catch (error) {
      toast.error("Failed to sync BSR data");
    } finally {
      setSyncing(false);
    }
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
  const filtered = products.filter(p => {
    const typeMatch = filter === "all" || p.product_type === filter;
    const catMatch = category === "all" || p.category === category;
    return typeMatch && catMatch && p.current_bsr;
  });

  if (loading) return <div className="text-gray-400 p-4">Loading BSR data...</div>;

  return (
    <div className="bg-white rounded-lg border">
      {/* Filters */}
      <div className="p-4 border-b flex flex-wrap gap-3 items-center">
        <h2 className="font-bold text-gray-800">📈 BSR Tracker</h2>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="text-sm border rounded px-2 py-1"
        >
          <option value="all">All Products</option>
          <option value="own">Own SKUs</option>
          <option value="competition">Competitors</option>
        </select>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="text-sm border rounded px-2 py-1"
        >
          <option value="all">All Categories</option>
          {categories.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="text-xs text-gray-400">
          {filtered.length} products with BSR data
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={handleSync}
          disabled={syncing}
          className="ml-auto"
        >
          <RefreshCw className={cn("h-4 w-4 mr-1", syncing && "animate-spin")} />
          {syncing ? "Syncing..." : "Sync BSR"}
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
              <th className="text-left p-3">SKU</th>
              <th className="text-left p-3">Category</th>
              <th className="text-center p-3">Category Rank</th>
              <th className="text-center p-3">BSR Today</th>
              <th className="text-center p-3">BSR 7d Ago</th>
              <th className="text-center p-3">Trend</th>
              <th className="text-center p-3">Est. Units/Mo</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-gray-400">
                  No BSR data available. Click "Sync BSR" to fetch latest data.
                </td>
              </tr>
            ) : (
              filtered.map((p, i) => {
                const bsrChange = p.bsr_7d_ago && p.current_bsr
                  ? p.bsr_7d_ago - p.current_bsr
                  : null;
                return (
                  <tr
                    key={p.asin}
                    className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50`}
                  >
                    <td className="p-3">
                      <div className="font-medium">{p.name?.substring(0, 40)}...</div>
                      <div className="text-xs text-gray-400">{p.asin}</div>
                    </td>
                    <td className="p-3 text-gray-600">{p.category}</td>
                    <td className="p-3 text-center">
                      {p.rank_in_category ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                          p.rank_in_category <= 3 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : p.rank_in_category <= 10 
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          #{p.rank_in_category} / {p.total_in_category}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="p-3 text-center">
                      <div className="font-mono font-semibold text-gray-800">
                        #{p.current_bsr?.toLocaleString() || "—"}
                      </div>
                      {p.bsr_category && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          in {p.bsr_category}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-center font-mono text-gray-400">
                      #{p.bsr_7d_ago?.toLocaleString() || "—"}
                    </td>
                    <td className="p-3 text-center">
                      <BSRTrendBadge trend={p.bsr_trend_7d || p.trend_7d} />
                      {bsrChange !== null && (
                        <div className={`text-xs mt-1 ${bsrChange > 0 ? "text-green-600" : "text-red-600"}`}>
                          {bsrChange > 0 ? `+${bsrChange.toLocaleString()}` : bsrChange.toLocaleString()} rank
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-center text-gray-600">
                      ~{p.est_monthly_units?.toLocaleString() || p.estimated_monthly_units?.toLocaleString() || "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Main BSR Tracker Page ────────────────────────────────────────────────────
export default function BSRTracker() {
  const [selectedProduct, setSelectedProduct] = useState(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1a1814]">BSR Tracker</h1>
        <p className="text-sm text-[#8a8278]">
          Best Seller Rank tracking with trend analysis
        </p>
      </div>

      {/* BSR Summary Table */}
      <BSRSummaryTable />

      {/* BSR History Chart (shown when product selected) */}
      {selectedProduct && (
        <BSRHistoryChart
          asin={selectedProduct.asin}
          productName={selectedProduct.name}
        />
      )}
    </div>
  );
}
